import type { PaymentKitErrors, PaymentKitStates, TInternalFuncs } from "../types";
import { collectFraudMetadata, definePaymentMethod, getOrCreateCheckoutRequestId } from "../utils";
import {
  AirwallexGooglePayAdapter,
  type AirwallexGooglePayConfig,
  AirwallexGooglePayMockScenario,
  type GooglePayEncryptedToken,
} from "./airwallex-google-pay-adapter";
import { handleNextAction } from "./next-action-handlers";
import { GooglePayMockScenario, StripeGooglePayAdapter } from "./stripe-google-pay-adapter";

// Google Pay-specific types
export type GooglePayCustomerInfo = {
  first_name: string;
  last_name: string;
  email?: string;
};

export type GooglePayStartRequest = {
  processor_id: string;
  customer_info: GooglePayCustomerInfo;
  fraud_metadata: {
    ipAddress?: string;
    browserInfo?: { [key: string]: unknown };
    processorFraudInfo?: { [key: string]: unknown };
  };
  mock_scenario?: string;
};

export type GooglePayStartResponse = {
  checkout_attempt_id: string;
  amount: number;
  amount_display: string; // Formatted for Google Pay (e.g., "100.00" for USD, "1000" for JPY)
  currency: string;
  country: string;

  // Processor discriminator
  processor: "stripe" | "airwallex";

  // Environment flag
  is_sandbox?: boolean;

  // Stripe-specific fields (when processor="stripe")
  client_secret?: string;
  stripe_pk?: string;

  // Airwallex-specific fields (when processor="airwallex")
  merchant_name?: string;
  airwallex_account_id?: string;
  google_merchant_id?: string; // Google-assigned merchant ID for production (BCR2DN...)
};

export type GooglePayConfirmRequest = {
  google_pay_token?: GooglePayEncryptedToken; // Required for Airwallex
  payer_email?: string; // Email from payment sheet (Stripe Google Pay)
  mock_scenario?: string;
};

// Airwallex 3DS next action type (matches backend snake_case response)
export type Airwallex3dsNextAction = {
  type: "airwallex_3ds";
  url: string;
  method: string;
  payment_intent_id: string;
};

export type GooglePayConfirmResponse = {
  charge_status: "success" | "fail" | "pending";
  transaction_id?: string;
  error_code?: string;
  error_message?: string;
  error_message_for_customer?: string;
  error_message_for_debug?: string;
  checkout_attempt_id: string;
  checkout_session_id?: string;
  next_action?: Airwallex3dsNextAction; // Present when charge_status="pending" for 3DS
  payment_intent_id?: string;
  customer_id?: string;
  payment_method_id?: string;
  processor_used?: string;
  subscription_id?: string;
  invoice_id?: string;
  invoice_number?: number;
  card_brand?: string;
  card_last4?: string;
  card_exp_month?: number;
  card_exp_year?: number;
};

export type GooglePaySubmitOptions = {
  /** Processor external ID — optional when using auto-prepare (SDK fetches from checkout session) */
  processorId?: string;
  customerInfo: GooglePayCustomerInfo;
  mockScenario?: GooglePayMockScenario;
};

type GooglePayResult =
  | { data: { [key: string]: unknown }; errors?: never }
  | { data?: never; errors: PaymentKitErrors };

// =============================================================================
// Helper Functions
// =============================================================================

async function apiCall<T>(
  url: string,
  options: RequestInit,
  checkoutRequestId?: string,
): Promise<{ data?: T; error?: string }> {
  // Add x-request-id header if provided
  const headers = new Headers(options.headers);
  if (checkoutRequestId) {
    headers.set("x-request-id", checkoutRequestId);
  }

  const response = await fetch(url, { ...options, headers });
  if (!response.ok) {
    let errorMessage = `Request failed (${response.status})`;
    try {
      const errorData = await response.json();
      errorMessage = errorData.detail || errorMessage;
    } catch {
      errorMessage = response.statusText || errorMessage;
    }
    return { error: errorMessage };
  }
  return { data: await response.json() };
}

function validateOptions(
  options: GooglePaySubmitOptions,
  autoPrepareConfig: AutoPrepareConfig | null,
): PaymentKitErrors | null {
  // processorId is optional when autoPrepareConfig provides it
  if (!options?.processorId && !autoPrepareConfig?.processorId) {
    return { processor_id: "Processor ID is required" };
  }
  // Customer name is optional — Google Pay collects it from the payment sheet
  return null;
}

// =============================================================================
// Auto-Prepare Types
// =============================================================================

type CheckoutSessionConfig = {
  express_checkout_processor_id?: string | null;
  express_checkout_processor_type?: string | null;
};

type AutoPrepareConfig = {
  processorId: string;
  processorType?: "stripe" | "airwallex";
};

/**
 * Check if Google Pay is available on this device/browser.
 * Uses the Google Pay API's isReadyToPay() method.
 */
async function checkGooglePayAvailability(): Promise<boolean> {
  // Check if Google Pay API is loaded
  if (!window.google?.payments?.api?.PaymentsClient) {
    console.log("[GooglePay] Google Pay API not loaded");
    return false;
  }

  try {
    // Per Google Pay docs, TEST mode is appropriate for isReadyToPay() availability checks.
    // The actual payment flow uses the production environment configured in submit().
    const client = new window.google.payments.api.PaymentsClient({
      environment: "TEST",
    });

    const request = {
      apiVersion: 2,
      apiVersionMinor: 0,
      allowedPaymentMethods: [
        {
          type: "CARD" as const,
          parameters: {
            allowedAuthMethods: ["PAN_ONLY", "CRYPTOGRAM_3DS"] as ("PAN_ONLY" | "CRYPTOGRAM_3DS")[],
            allowedCardNetworks: ["VISA", "MASTERCARD", "AMEX", "DISCOVER", "JCB"] as (
              | "VISA"
              | "MASTERCARD"
              | "AMEX"
              | "DISCOVER"
              | "JCB"
            )[],
          },
        },
      ],
    };

    const response = await client.isReadyToPay(request);
    return response.result;
  } catch (error) {
    console.error("[GooglePay] availability check error:", error);
    return false;
  }
}

function getMockScenarioStr(mockScenario?: GooglePayMockScenario): string | undefined {
  return mockScenario && mockScenario !== GooglePayMockScenario.None ? mockScenario : undefined;
}

async function callStartEndpoint(
  apiBaseUrl: string,
  secureToken: string,
  options: GooglePaySubmitOptions,
  mockScenarioStr?: string,
  checkoutRequestId?: string,
): Promise<{ data?: GooglePayStartResponse; error?: string }> {
  return apiCall<GooglePayStartResponse>(
    `${apiBaseUrl}/api/checkout/${secureToken}/google-pay/start`,
    {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({
        processor_id: options.processorId,
        customer_info: options.customerInfo,
        fraud_metadata: collectFraudMetadata(),
        mock_scenario: mockScenarioStr,
      } as GooglePayStartRequest),
    },
    checkoutRequestId,
  );
}

// =============================================================================
// Stripe Flow
// =============================================================================

function initializeStripeAdapter(
  stripePk: string,
  startData: GooglePayStartResponse,
  mockScenario?: GooglePayMockScenario,
): { adapter?: StripeGooglePayAdapter; error?: string } {
  const adapter = new StripeGooglePayAdapter(mockScenario);

  if (!adapter.initialize(stripePk)) {
    return { error: 'Stripe.js not loaded. Add <script src="https://js.stripe.com/v3/"></script> to your page.' };
  }

  const prConfig = {
    country: startData.country,
    currency: startData.currency.toLowerCase(),
    total: { label: "Total", amount: startData.amount },
    requestPayerName: true,
    requestPayerEmail: true,
  };
  adapter.createPaymentRequest(prConfig);
  return { adapter };
}

async function runStripeFlow(
  startData: GooglePayStartResponse,
  mockScenario?: GooglePayMockScenario,
): Promise<{ success: boolean; error?: string; payerEmail?: string }> {
  if (!startData.stripe_pk || !startData.client_secret) {
    return { success: false, error: "Stripe credentials not provided" };
  }

  // Initialize Stripe adapter
  const { adapter, error: adapterError } = initializeStripeAdapter(startData.stripe_pk, startData, mockScenario);
  if (!adapter) {
    return { success: false, error: adapterError };
  }

  // Check availability
  const isAvailable = await adapter.canMakePayment();

  if (!isAvailable) {
    return { success: false, error: "Google Pay not available on this device" };
  }

  // Show payment sheet
  const paymentResult = await adapter.showPaymentSheet();

  if (!paymentResult.success) {
    if ("cancelled" in paymentResult && paymentResult.cancelled) {
      return { success: false, error: "Google Pay cancelled by user" };
    }
    const errorMessage = "error" in paymentResult ? paymentResult.error : "Unknown error";
    return { success: false, error: errorMessage };
  }

  // Confirm with Stripe SDK
  const confirmResult = await adapter.confirmCardSetup(startData.client_secret, paymentResult.paymentMethodId);

  if (!confirmResult.success) {
    paymentResult.complete("fail");
    return { success: false, error: "error" in confirmResult ? confirmResult.error : "Card setup failed" };
  }

  paymentResult.complete("success");
  return { success: true, payerEmail: paymentResult.payerEmail };
}

// =============================================================================
// Airwallex Flow
// =============================================================================

function initializeAirwallexAdapter(
  startData: GooglePayStartResponse,
  mockScenario?: GooglePayMockScenario,
): {
  adapter?: AirwallexGooglePayAdapter;
  error?: string;
} {
  if (!startData.merchant_name || !startData.airwallex_account_id) {
    return { error: "Airwallex Google Pay credentials not provided" };
  }

  // Convert GooglePayMockScenario to AirwallexGooglePayMockScenario
  let airwallexMockScenario: AirwallexGooglePayMockScenario = AirwallexGooglePayMockScenario.None;
  if (mockScenario === GooglePayMockScenario.Success) {
    airwallexMockScenario = AirwallexGooglePayMockScenario.Success;
  } else if (mockScenario === GooglePayMockScenario.Cancelled) {
    airwallexMockScenario = AirwallexGooglePayMockScenario.Cancelled;
  }

  const adapter = new AirwallexGooglePayAdapter(airwallexMockScenario);

  const config: AirwallexGooglePayConfig = {
    merchantId: startData.merchant_name,
    gatewayMerchantId: startData.airwallex_account_id,
    amountDisplay: startData.amount_display,
    currency: startData.currency,
    country: startData.country,
    isProduction: startData.is_sandbox === false,
    googleMerchantId: startData.google_merchant_id,
  };

  if (!adapter.initialize(config)) {
    return {
      error:
        'Google Pay API not loaded. Add <script src="https://pay.google.com/gp/p/js/pay.js"></script> to your page.',
    };
  }

  return { adapter };
}

async function runAirwallexFlow(
  startData: GooglePayStartResponse,
  mockScenario?: GooglePayMockScenario,
): Promise<{ success: boolean; token?: GooglePayEncryptedToken; payerEmail?: string; error?: string }> {
  // Initialize Airwallex adapter
  const { adapter, error: adapterError } = initializeAirwallexAdapter(startData, mockScenario);
  if (!adapter) {
    return { success: false, error: adapterError };
  }

  // Check availability
  const isAvailable = await adapter.canMakePayment();

  if (!isAvailable) {
    return { success: false, error: "Google Pay not available on this device" };
  }

  // Show payment sheet
  const paymentResult = await adapter.showPaymentSheet();

  if (!paymentResult.success) {
    if ("cancelled" in paymentResult && paymentResult.cancelled) {
      return { success: false, error: "Google Pay cancelled by user" };
    }
    const errorMessage = "error" in paymentResult ? paymentResult.error : "Unknown error";
    return { success: false, error: errorMessage };
  }

  // Return the token and payer email for passing to backend
  return { success: true, token: paymentResult.token, payerEmail: paymentResult.payerEmail };
}

// =============================================================================
// Confirm & Verify Endpoints
// =============================================================================

/**
 * Call /confirm endpoint - processes Google Pay token (Airwallex) or records setup (Stripe).
 * For Airwallex, may return next_action if 3DS is required.
 */
async function callConfirmEndpoint(
  apiBaseUrl: string,
  secureToken: string,
  googlePayToken?: GooglePayEncryptedToken,
  mockScenarioStr?: string,
  checkoutRequestId?: string,
  payerEmail?: string,
): Promise<GooglePayConfirmResponse> {
  const requestBody: GooglePayConfirmRequest = {
    mock_scenario: mockScenarioStr,
  };

  if (googlePayToken) {
    requestBody.google_pay_token = googlePayToken;
  }

  if (payerEmail) {
    requestBody.payer_email = payerEmail;
  }

  const result = await apiCall<GooglePayConfirmResponse>(
    `${apiBaseUrl}/api/checkout/${secureToken}/google-pay/confirm`,
    {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify(requestBody),
    },
    checkoutRequestId,
  );

  if (result.error || !result.data) {
    return {
      charge_status: "fail",
      error_message: result.error || "Failed to confirm payment",
      checkout_attempt_id: "",
    };
  }

  return result.data;
}

/**
 * Call /verify endpoint - verifies 3DS completion for Airwallex.
 * Called after user completes 3DS challenge. May return another next_action for retry.
 */
async function callVerifyEndpoint(
  apiBaseUrl: string,
  secureToken: string,
  checkoutRequestId?: string,
): Promise<GooglePayConfirmResponse> {
  const result = await apiCall<GooglePayConfirmResponse>(
    `${apiBaseUrl}/api/checkout/${secureToken}/google-pay/verify`,
    {
      method: "POST",
      headers: { "Content-Type": "application/json" },
    },
    checkoutRequestId,
  );

  if (result.error || !result.data) {
    return {
      charge_status: "fail",
      error_message: result.error || "Failed to verify payment",
      checkout_attempt_id: "",
    };
  }

  return result.data;
}

/**
 * Convert GooglePayConfirmResponse to GooglePayResult for return to caller.
 */
function toGooglePayResult(response: GooglePayConfirmResponse, secureToken: string): GooglePayResult {
  if (response.charge_status === "success") {
    return {
      data: {
        id: response.transaction_id,
        checkoutAttemptId: response.checkout_attempt_id,
        checkoutSessionId: response.checkout_session_id ?? secureToken,
        state: "checkout_succeeded",
        paymentIntentId: response.payment_intent_id,
        customerId: response.customer_id,
        paymentMethodId: response.payment_method_id,
        processorUsed: response.processor_used,
        subscriptionId: response.subscription_id,
        invoiceId: response.invoice_id,
        invoiceNumber: response.invoice_number,
        cardBrand: response.card_brand,
        cardLast4: response.card_last4,
        cardExpMonth: response.card_exp_month,
        cardExpYear: response.card_exp_year,
        errorCode: response.error_code,
        errorMessageForCustomer: response.error_message_for_customer,
        errorMessageForDebug: response.error_message_for_debug,
        nextAction: response.next_action,
      },
    };
  }
  return {
    errors: {
      google_pay:
        response.error_message_for_customer ||
        response.error_message_for_debug ||
        response.error_message ||
        "Payment failed",
    },
  };
}

// =============================================================================
// Stripe Confirm (Simple - No 3DS Loop)
// =============================================================================

/**
 * Confirm Stripe Google Pay - simple flow without 3DS loop.
 * Stripe.js handles 3DS internally during confirmCardSetup().
 */
async function confirmStripeGooglePay(
  apiBaseUrl: string,
  secureToken: string,
  mockScenarioStr?: string,
  checkoutRequestId?: string,
  payerEmail?: string,
): Promise<GooglePayResult> {
  const response = await callConfirmEndpoint(
    apiBaseUrl,
    secureToken,
    undefined,
    mockScenarioStr,
    checkoutRequestId,
    payerEmail,
  );
  return toGooglePayResult(response, secureToken);
}

// =============================================================================
// Airwallex Confirm with 3DS Loop
// =============================================================================

const MAX_USER_ACTIONS = 5;

/**
 * Confirm Airwallex Google Pay with 3DS loop support.
 *
 * Flow:
 * 1. Call /confirm with token → may return next_action (3DS)
 * 2. If next_action: handle 3DS, then call /verify
 * 3. Loop if /verify returns another next_action (3DS retry)
 * 4. Return final result
 *
 * This mirrors the card.ts pattern for consistency.
 */
async function confirmAirwallexGooglePay(
  apiBaseUrl: string,
  secureToken: string,
  googlePayToken: GooglePayEncryptedToken,
  mockScenarioStr?: string,
  checkoutRequestId?: string,
  payerEmail?: string,
): Promise<GooglePayResult> {
  let userActionCount = 0;

  // Step 1: Initial confirm with token
  let response = await callConfirmEndpoint(
    apiBaseUrl,
    secureToken,
    googlePayToken,
    mockScenarioStr,
    checkoutRequestId,
    payerEmail,
  );

  // Step 2: Handle 3DS loop (if required)
  while (response.charge_status === "pending" && response.next_action && userActionCount < MAX_USER_ACTIONS) {
    userActionCount++;

    // Handle 3DS challenge
    const actionResult = await handleNextAction(response.next_action);

    // Always call verify endpoint so backend can conclude properly
    // (even if 3DS failed, backend needs to know)
    const verifyResponse = await callVerifyEndpoint(apiBaseUrl, secureToken, checkoutRequestId);

    // Check if another 3DS action is required (retry scenario)
    if (verifyResponse.charge_status === "pending" && verifyResponse.next_action) {
      if (!actionResult.success) {
        console.log("[GooglePay:Airwallex] 3DS failed but retry available, continuing loop...");
      }
      response = verifyResponse;
      continue;
    }

    // No more actions - check final result
    if (!actionResult.success) {
      // 3DS failed and no retry available
      return {
        errors: {
          google_pay: verifyResponse.error_message || actionResult.error || "3DS authentication failed",
        },
      };
    }

    // 3DS succeeded - return verify result
    return toGooglePayResult(verifyResponse, secureToken);
  }

  // Check for max attempts exceeded
  if (userActionCount >= MAX_USER_ACTIONS) {
    return { errors: { google_pay: "Too many authentication attempts. Please try again." } };
  }

  // No 3DS required - return initial confirm result
  return toGooglePayResult(response, secureToken);
}

// =============================================================================
// Main Submit Function
// =============================================================================

const defSubmitPayment = (states: PaymentKitStates, getAutoPrepareConfig: () => AutoPrepareConfig | null) => {
  const submitPayment: TInternalFuncs["submitPayment"] = async (_fields, options) => {
    const { apiBaseUrl, secureToken, environment } = states;
    const gpayOptions = options as GooglePaySubmitOptions;
    const autoPrepareConfig = getAutoPrepareConfig();

    // Validate options
    const validationError = validateOptions(gpayOptions, autoPrepareConfig);
    if (validationError) {
      return { errors: validationError };
    }

    // Use autoPrepareConfig as fallback for processorId
    const effectiveOptions: GooglePaySubmitOptions = {
      ...gpayOptions,
      processorId: gpayOptions.processorId || autoPrepareConfig?.processorId || "",
    };

    try {
      const mockScenarioStr = getMockScenarioStr(gpayOptions.mockScenario);

      // Get or create checkout request ID for correlating all API calls
      const checkoutRequestId = getOrCreateCheckoutRequestId(environment);
      console.log(`[GooglePay] Using checkout_request_id: ${checkoutRequestId}`);

      // Step 1: Start Google Pay flow - backend selects processor
      const startResult = await callStartEndpoint(
        apiBaseUrl,
        secureToken,
        effectiveOptions,
        mockScenarioStr,
        checkoutRequestId,
      );
      if (startResult.error || !startResult.data) {
        return { errors: { google_pay: startResult.error || "Failed to start Google Pay" } };
      }

      const startData = startResult.data;

      // Step 2 & 3: Run processor-specific flow and confirm
      if (startData.processor === "stripe") {
        // Stripe flow: Uses Stripe.js PaymentRequest API (handles 3DS internally)
        const stripeResult = await runStripeFlow(startData, gpayOptions.mockScenario);
        if (!stripeResult.success) {
          return { errors: { google_pay: stripeResult.error } };
        }
        // Confirm with backend (no token needed, Stripe handled it)
        return await confirmStripeGooglePay(
          apiBaseUrl,
          secureToken,
          mockScenarioStr,
          checkoutRequestId,
          stripeResult.payerEmail,
        );
      }

      if (startData.processor === "airwallex") {
        // Airwallex flow: Uses Google Pay API directly, may need 3DS
        const airwallexResult = await runAirwallexFlow(startData, gpayOptions.mockScenario);
        if (!airwallexResult.success) {
          return { errors: { google_pay: airwallexResult.error } };
        }
        if (!airwallexResult.token) {
          return { errors: { google_pay: "Google Pay token not received" } };
        }
        // Confirm with 3DS loop support
        return await confirmAirwallexGooglePay(
          apiBaseUrl,
          secureToken,
          airwallexResult.token,
          mockScenarioStr,
          checkoutRequestId,
          airwallexResult.payerEmail,
        );
      }

      return { errors: { google_pay: `Unsupported processor: ${startData.processor}` } };
    } catch (error) {
      return { errors: { google_pay: `Google Pay error: ${error}` } };
    }
  };

  return submitPayment;
};

// =============================================================================
// Payment Method Definition
// =============================================================================

const GooglePayPaymentMethod = definePaymentMethod((paymentKitStates) => {
  const { apiBaseUrl, secureToken, environment } = paymentKitStates;

  // Per-instance auto-prepare state — isolated from other GooglePayPaymentMethod instances.
  let autoPrepareConfig: AutoPrepareConfig | null = null;
  let onGooglePayReadyCallbacks: ((isReady: boolean) => void)[] = [];
  let isGooglePayReadyState = false;
  let autoPrepareInProgress = false;
  // Incremented by cleanup() so that in-flight prepares can detect staleness and skip writes.
  let cleanupGeneration = 0;
  // Stores pending retry args when notifyAmountChanged is called while a prepare is in flight.
  let pendingAutoPrepareArgs: { apiBaseUrl: string; secureToken: string; environment: string } | null = null;
  // Resolvers waiting for a queued retry to complete (for correct notifyAmountChanged await).
  let pendingPrepareResolvers: (() => void)[] = [];

  function instanceSetGooglePayReady(ready: boolean): void {
    isGooglePayReadyState = ready;
    for (const cb of onGooglePayReadyCallbacks) cb(ready);
  }

  function instanceClearPreparedGooglePay(): void {
    instanceSetGooglePayReady(false);
  }

  async function instanceAutoPrepare(apBase: string, sToken: string, env: string): Promise<void> {
    if (autoPrepareInProgress) {
      // A prepare is already in flight; queue a retry and return a promise that resolves after it.
      pendingAutoPrepareArgs = { apiBaseUrl: apBase, secureToken: sToken, environment: env };
      return new Promise<void>((resolve) => pendingPrepareResolvers.push(resolve));
    }
    autoPrepareInProgress = true;
    // Capture generation so we can detect if cleanup() is called while we await.
    const capturedGeneration = cleanupGeneration;
    instanceSetGooglePayReady(false);

    try {
      const result = await apiCall<CheckoutSessionConfig>(`${apBase}/api/checkout-sessions/token/${sToken}`, {
        method: "GET",
      });

      if (cleanupGeneration !== capturedGeneration) return;

      if (result.error || !result.data) {
        console.warn("[GooglePay] Auto-prepare: failed to fetch session config:", result.error);
        return;
      }

      const processorId = result.data.express_checkout_processor_id;
      if (!processorId) {
        console.log("[GooglePay] Auto-prepare: no express checkout processor configured");
        return;
      }

      const processorTypeRaw = result.data.express_checkout_processor_type;
      const processorType =
        processorTypeRaw === "airwallex" || processorTypeRaw === "stripe" ? processorTypeRaw : undefined;

      autoPrepareConfig = { processorId, processorType };

      const isAvailable = await checkGooglePayAvailability();

      if (cleanupGeneration !== capturedGeneration) return;

      instanceSetGooglePayReady(isAvailable);
    } catch (err) {
      if (cleanupGeneration === capturedGeneration) {
        console.error("[GooglePay] Auto-prepare failed:", err);
      }
    } finally {
      autoPrepareInProgress = false;
      const stale = cleanupGeneration !== capturedGeneration;
      const pending = pendingAutoPrepareArgs;
      const resolvers = pendingPrepareResolvers.splice(0);
      if (!stale && pending) {
        pendingAutoPrepareArgs = null;
        // Run the retry and resolve all callers that were waiting for it.
        instanceAutoPrepare(pending.apiBaseUrl, pending.secureToken, pending.environment).then(() => {
          for (const r of resolvers) r();
        });
      } else {
        pendingAutoPrepareArgs = null;
        for (const r of resolvers) r();
      }
    }
  }

  // Start auto-prepare immediately so the button is ready before first user interaction.
  instanceAutoPrepare(apiBaseUrl, secureToken, environment).catch((err) =>
    console.error("[GooglePay] Unhandled init error:", err),
  );

  return {
    name: "google_pay",
    externalFuncs: {
      /**
       * Register a callback to be notified when Google Pay becomes ready (or stops being ready).
       * Fires immediately with the current state, then on every state change.
       * Multiple registrations are all notified — none are silently overwritten.
       *
       * @example
       * paymentKit.google_pay.onGooglePayReady((isReady) => {
       *   googlePayButton.disabled = !isReady;
       * });
       */
      onGooglePayReady: (callback: (isReady: boolean) => void): void => {
        onGooglePayReadyCallbacks.push(callback);
        // Fire immediately with current state so caller doesn't miss a completed prepare.
        callback(isGooglePayReadyState);
      },

      /**
       * Notify the SDK that the payment amount has changed (e.g. coupon applied).
       * The SDK will disable Google Pay, re-prepare with the new amount, then re-enable.
       * The returned promise resolves when re-prepare is fully complete.
       *
       * @example
       * await paymentKit.google_pay.notifyAmountChanged();
       */
      notifyAmountChanged: (): Promise<void> => {
        instanceClearPreparedGooglePay();
        autoPrepareConfig = null;
        return instanceAutoPrepare(apiBaseUrl, secureToken, environment);
      },
    },
    internalFuncs: {
      submitPayment: defSubmitPayment(paymentKitStates, () => autoPrepareConfig),
      cleanup: () => {
        cleanupGeneration++;
        onGooglePayReadyCallbacks = [];
        isGooglePayReadyState = false;
        autoPrepareConfig = null;
        pendingAutoPrepareArgs = null;
        pendingPrepareResolvers = [];
        autoPrepareInProgress = false;
      },
    },
  };
});

export { GooglePayMockScenario };

export default GooglePayPaymentMethod;
